import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { AccountHeaderItemType } from '@digital-platform/rs-header-services';
import { AccountHeaderItemComponent } from '../account-header-item/account-header-item.component';

/**
 * Component to render the account header with a list of items.
 * The component uses an IntersectionObserver to determine if the header should be sticky.
 */
@Component({
  selector: 'digital-platform-account-header',
  standalone: true,
  imports: [CommonModule, AccountHeaderItemComponent],
  templateUrl: './account-header.component.html',
  styleUrls: ['./account-header.component.scss'],
})
export class AccountHeaderComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  public items: AccountHeaderItemType[] = [];
  public sticky = false;

  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  private intersectionObserver?: IntersectionObserver;

  @ViewChild('dummyRef')
  elementRef!: ElementRef<HTMLElement> | undefined;

  ngOnInit(): void {
    if (this.isBrowser) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.sticky = false;
          } else {
            this.sticky = true;
          }
        });
      });
    }
    // get the list of items to be renders as part of the header
    this.activatedRoute.data.subscribe((data) => {
      if (data['accountHeaderItems']) {
        this.items = data['accountHeaderItems'];
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.elementRef && this.isBrowser) {
      this.intersectionObserver?.observe(this.elementRef.nativeElement);
    }
  }

  onItemClicked(selectedItem: AccountHeaderItemType) {
    // update the selected item based on the click
    this.items = this.items.map((item) => {
      if (item.name === selectedItem.name) {
        return { ...item, selected: true };
      }
      return { ...item, selected: false };
    });

    if (selectedItem.url) {
      this.router.navigate([selectedItem.url]);
    }
  }

  ngOnDestroy(): void {
    if (this.isBrowser) {
      this.intersectionObserver?.disconnect();
    }
  }
}
